'use client';

import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';

import { Dialog } from 'radix-ui';

import { Button } from '@/components/common/button/Button';
import { Checkbox } from '@/components/common/Checkbox';
import { Flex } from '@/components/common/Flex';
import { Text } from '@/components/common/Text';

import type { CookieConsentPreferences } from './cookieConsentStorage';

import styles from './CookieSettingsDialog.module.scss';

export type CookieSettingsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPreferences: CookieConsentPreferences;
  onSave: (prefs: CookieConsentPreferences) => void;
};

export const CookieSettingsDialog: FC<CookieSettingsDialogProps> = (props) => {
  const { open, onOpenChange, initialPreferences, onSave } = props;
  const t = useTranslations('cookieConsent');

  const [analytics, setAnalytics] = useState(initialPreferences.analytics);
  const [marketing, setMarketing] = useState(initialPreferences.marketing);

  useEffect(() => {
    if (open) {
      setAnalytics(initialPreferences.analytics);
      setMarketing(initialPreferences.marketing);
    }
  }, [open, initialPreferences.analytics, initialPreferences.marketing]);

  const handleSave = (): void => {
    onSave({ analytics, marketing });
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.inner}>
            <Dialog.Title className={styles.dialogTitle}>
              <Text type="regular18">{t('settingsTitle')}</Text>
            </Dialog.Title>

            <Dialog.Description className={styles.description}>{t('settingsDescription')}</Dialog.Description>

            <div className={styles.rowGap}>
              <Checkbox
                inputId="cookie-necessary"
                checked
                disabled
                label={
                  <div className={styles.labelCol}>
                    <Text type="regular14">{t('necessary')}</Text>
                    <Text type="regular12" className={styles.hint}>
                      {t('necessaryHint')}
                    </Text>
                  </div>
                }
              />

              <Checkbox
                inputId="cookie-analytics"
                checked={analytics}
                onChange={setAnalytics}
                label={
                  <div className={styles.labelCol}>
                    <Text type="regular14">{t('analytics')}</Text>
                    <Text type="regular12" className={styles.hint}>
                      {t('analyticsHint')}
                    </Text>
                  </div>
                }
              />

              <Checkbox
                inputId="cookie-marketing"
                checked={marketing}
                onChange={setMarketing}
                label={
                  <div className={styles.labelCol}>
                    <Text type="regular14">{t('marketing')}</Text>
                    <Text type="regular12" className={styles.hint}>
                      {t('marketingHint')}
                    </Text>
                  </div>
                }
              />
            </div>

            <Flex className={styles.actions} gap={12} wrap="wrap" justifyContent="flexEnd">
              <Dialog.Close asChild>
                <Button type="button" variant="secondary">
                  {t('cancel')}
                </Button>
              </Dialog.Close>

              <Button type="button" variant="main" onClick={handleSave}>
                {t('savePreferences')}
              </Button>
            </Flex>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
